import React, { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { Box, TextV2, TooltipV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { cashFundCurrencyPromptVar } from '@shared/utils/cashFund/cashFundCurrencyPromptVar';

/**
 * Wraps a currency picker with the "switch currency to use your honeymoon fund" bubble,
 * open on arrival, when one is pending.
 *
 * With no prompt pending — every other page, every other user — it renders `children`
 * untouched, so wrapping a picker is safe anywhere.
 *
 * Two levels of dismissal, because the persisted one is permanent (a server-side consent
 * write, per user and per event, that survives every future session and device):
 *
 * - Clicking away or pressing Escape hides it for THIS prompt only, keyed on `prompt.key`,
 *   so the next hotel shows it again. A stray click must not cost the couple the message
 *   forever.
 * - Opening the picker, or taking the switch action, persists it. Both mean the couple has
 *   seen the message and acted on the currency, so there is nothing left to say.
 */
export const CashFundCurrencyPrompt = ({ children }: { children: React.ReactNode }) => {
  const prompt = useReactiveVar(cashFundCurrencyPromptVar);
  // Keyed, not a boolean: this component lives in the guest top bar on the V1 path, which
  // survives client-side navigation. A plain boolean would keep the prompt hidden for the
  // rest of the session after one click-away, on every later hotel.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  if (!prompt || dismissedKey === prompt.key) {
    return <>{children}</>;
  }

  return (
    <TooltipV2
      isOpen
      onClose={() => setDismissedKey(prompt.key)}
      placement="bottom"
      usePortal
      triggerType="click"
      content={
        // role="status" so the message is announced; an auto-opened tooltip is otherwise
        // silent to a screen reader. The action reads inline, right after the sentence, so
        // it stays part of the same thought rather than a separate block.
        <TextV2
          role="status"
          typographyVariant="label3"
          color="white"
          maxWidth={pxToRem(280)}
        >
          {prompt.message}{' '}
          <Box
            as="button"
            type="button"
            onClick={prompt.onSwitch}
            display="inline"
            background="none"
            border="none"
            padding={0}
            margin={0}
            cursor="pointer"
            color="white"
            textDecoration="underline"
            fontFamily="inherit"
            fontSize="inherit"
            fontWeight="inherit"
            lineHeight="inherit"
          >
            {prompt.actionLabel}
          </Box>
        </TextV2>
      }
    >
      {/* Opening the picker means the couple read the message and is choosing a currency,
          so that is the moment worth remembering. */}
      <Box onClick={prompt.onDismissForGood}>{children}</Box>
    </TooltipV2>
  );
};
